<template>
  <div class="download-page" ref="downloadContainer">
    <Header />

    <div class="download-content">
      <div class="container">
        <div class="download-header">
          <h1 class="download-title">安装咖喱阅卷</h1>
          <div class="browser-info">
            <!-- 当不是 Chrome 家族模式时，保留原先的“检测到浏览器”提示 -->
            <p class="detected-browser"
              v-if="!isChromeFamilyMode && currentDisplayName && currentDisplayName !== 'other'">
              检测到您的浏览器：{{ currentDisplayName }}
            </p>

            <!-- Chrome 家族模式下：展示 360安全、Chrome、360极速 三选一的选择器 -->
            <div v-if="isChromeFamilyMode" class="chrome-family-container">
              <div class="chrome-family-title">请选择你正在使用的浏览器</div>
              <div class="chrome-family-options">
                <button class="browser-option" :class="{ active: selectedBrowser === '360ee' }"
                  @click="selectBrowser('360ee')">
                  <div class="browser-icon-circle" :style="browserIconStyle('360ee')"></div>
                  <div class="browser-label">360安全</div>
                </button>
                <button class="browser-option" :class="{ active: selectedBrowser === 'chrome' }"
                  @click="selectBrowser('chrome')">
                  <div class="browser-icon-circle" :style="browserIconStyle('chrome')"></div>
                  <div class="browser-label">Chrome</div>
                </button>
                <button class="browser-option" :class="{ active: selectedBrowser === '360x' }"
                  @click="selectBrowser('360x')">
                  <div class="browser-icon-circle" :style="browserIconStyle('360x')"></div>
                  <div class="browser-label">360极速</div>
                </button>
              </div>
            </div>

            <!-- Version loading state -->
            <div v-if="versionLoading" class="version-loading">
              <span class="loading-spinner"></span>
              <span>正在获取版本信息...</span>
            </div>
            
            <!-- Version error state -->
            <div v-else-if="versionError" class="version-error">
              <div class="error-icon">⚠️</div>
              <div class="error-message">{{ versionError }}</div>
              <div class="error-hint">请刷新页面重试，或联系我们获取帮助</div>
            </div>
            
            <!-- Normal download UI (only when version is ready) -->
            <template v-else>
              <button class="download-btn" @click="startDownload" v-if="selectedBrowser !== 'safari'"
                :class="{ disabled: !currentBrowserConfig.download_url }">
                {{ currentBrowserConfig.button_text }}
              </button>
              <div class="download-subtitle">
                <div v-html="currentBrowserConfig.description"></div>
              </div>
            </template>
          </div>

          <!-- Edge 浏览器备用安装提示 - only show when version is ready -->
          <div class="edge-fallback-tip" v-if="versionReady && selectedBrowser === 'edge'">
            如果您无法在“前往安装”按钮完成安装，请点击👉 <a
              href="https://maplebolo.feishu.cn/docx/YxQzdoFZqoL9k2xPo5WcLbCunDd?from=from_copylink"
              target="_blank"><em>备用安装教程</em></a>
          </div>
        </div>


        <!-- 安装步骤 - only show when version is ready -->
        <div class="steps-container"
          v-if="versionReady && currentBrowserConfig && currentBrowserConfig.steps && currentBrowserConfig.steps.length > 0">
          <div v-for="(step, index) in currentBrowserConfig.steps" :key="index" class="step-item">
            <div class="step-image" v-if="step.image">
              <img :src="step.image" :alt="'第' + (index + 1) + '步'" />
            </div>
            <div class="step-content">
              <h3 class="step-title">{{ step.title }}</h3>
              <p class="step-description" v-html="step.description"></p>
            </div>
          </div>
        </div>

        <!-- 悬浮浏览器切换按钮 - 仅调试模式显示 -->
        <div class="floating-browser-selector" v-if="isDevelopment">
          <div class="floating-button" @click="toggleBrowserMenu" :class="{ active: showBrowserMenu }">
            <span class="browser-icon">🌐</span>
          </div>
          <div class="browser-menu" v-show="showBrowserMenu">
            <div class="browser-menu-header">切换浏览器</div>
            <button v-for="(browser, key) in $config.download_page.browsers" :key="key" class="browser-menu-item"
              :class="{ active: effectiveDetectedBrowser === key }" @click="simulateBrowser(key)">
              {{ browser.name }}
            </button>
          </div>
        </div>

        <!-- 正文底部信息 -->
        <div class="content-footer" v-if="$config && $config.download_page && $config.download_page.footer">
          <p class="footer-text" v-html="$config.download_page.footer.supported_browsers"></p>
        </div>
      </div>
    </div>

    <!-- Footer 独立在外，需要滚动才能看到 -->
    <div class="download-footer">
      <p class="footer-text">{{ $config.download_page.footer.switch_browser_prompt }}</p>
    </div>

    <Footer />
  </div>

  <!-- 移动端弹窗 -->
  <div v-if="isMobile" class="mobile-modal-overlay" @click.stop>
    <div class="mobile-modal-content">
      <div class="mobile-modal-icon">
        <img src="/images/download-mobile.svg" alt="移动端提示" />
      </div>
      <div class="mobile-modal-text">
        <h3 class="mobile-modal-title">请使用电脑端进行安装</h3>
        <p class="mobile-modal-description">
          咖喱阅卷目前仅支持电脑端安装，请前往电脑端打开本网页进行安装
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "./Header.vue";
import Footer from "./Footer.vue";
import appConfig from "@/config/appConfig";

export default {
  name: "Download",
  components: {
    Header,
    Footer,
  },
  data() {
    return {
      detectedBrowser: "other",
      selectedBrowser: "other",
      showBrowserMenu: false,
      // 调试：用于覆盖UA识别结果，使浮动菜单能"模拟真实识别场景"
      debugDetectedOverride: null,
      isMobile: false,
      browserDisplayNames: {
        edge: "Edge浏览器",
        qq: "QQ浏览器",
        "360ee": "360安全浏览器",
        "360x": "360极速浏览器",
        chrome: "Chrome浏览器",
        sogou: "搜狗浏览器",
        quark: "夸克浏览器",
        safari: "safari",
        other: "其他浏览器",
      },
    };
  },
  computed: {
    // Version loading/error states
    versionLoading() {
      return appConfig.config.loading;
    },
    versionError() {
      return appConfig.config.error;
    },
    versionReady() {
      return appConfig.computed.isReady;
    },
    currentBrowserConfig() {
      if (!this.$config || !this.$config.download_page || !this.$config.download_page.browsers) {
        return { name: 'Unknown', title: 'Loading...', steps: [] };
      }
      // 对 360 极速单独给出“不支持”的文案
      // if (this.selectedBrowser === '360x') {
      //   return {
      //     name: '360极速浏览器',
      //     button_text: '无法安装',
      //     download_url: null,
      //     description: '360极速浏览器暂不支持安装，请选择 360安全 或 Chrome 浏览器完成安装',
      //     steps: [],
      //   };
      // }
      return (
        this.$config.download_page.browsers[this.selectedBrowser] ||
        this.$config.download_page.browsers.other ||
        { name: 'Unknown', title: 'Loading...', steps: [] }
      );
    },
    isDevelopment() {
      return process.env.NODE_ENV === "development";
    },
    // 生效中的“被识别浏览器”（UA识别 or 调试覆盖）
    effectiveDetectedBrowser() {
      return this.isDevelopment && this.debugDetectedOverride
        ? this.debugDetectedOverride
        : this.detectedBrowser;
    },
    isChromeFamilyMode() {
      // 由于 360 安全、360 极速可能识别为 chrome，这里统一在 chrome 下展示三选一
      return this.effectiveDetectedBrowser === 'chrome';
    },
    currentDisplayName() {
      // Prefer local names to ensure unsupported detected browsers (e.g., 360极速) still show proper name
      const byLocal = this.browserDisplayNames[this.selectedBrowser];
      if (byLocal) return byLocal;
      const cfg = this.$config && this.$config.download_page && this.$config.download_page.browsers
        ? this.$config.download_page.browsers[this.selectedBrowser]
        : null;
      return (cfg && cfg.name) || (this.$config && this.$config.download_page && this.$config.download_page.browsers.other && this.$config.download_page.browsers.other.name) || 'other';
    }
  },
  mounted() {
    this.detectBrowser();
    this.detectMobile();
    // 点击外部关闭菜单
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    // Detect if current device is mobile
    detectMobile() {
      const ua = navigator.userAgent || navigator.vendor || window.opera || '';
      const isMobile = /android|avantgo|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(ua);

      // Also check screen width for tablets and small screens
      const isSmallScreen = window.innerWidth <= 768;

      this.isMobile = isMobile || isSmallScreen;
    },
    // Return inline background-image style for browser icons in public/images
    browserIconStyle(key) {
      const map = {
        '360ee': '/images/browser-360ee.svg',
        '360x': '/images/browser-360x.svg',
        'chrome': '/images/browser-chrome.svg',
      };
      const url = map[key] || '';
      return url ? { backgroundImage: `url(${url})` } : {};
    },
    detectBrowser() {
      const ua = (navigator.userAgent || "").toLowerCase();
      console.log("userAgent: ", ua);
      let detected = "other";

      // 1. Check for browsers with distinct user agents that are not chrome-like
      // or have very clear identifiers.
      if (ua.includes("firefox")) {
        detected = "firefox";
      } else if (ua.includes("qqbrowser")) {
        detected = "qq";
      } else if (ua.includes("quark")) {
        detected = "quark";
      } else if (ua.includes("metasr") || ua.includes("sogou")) {
        detected = "sogou";
      } else if (ua.includes("edg/")) {
        // Edge is chromium, but has a clear "edg/" identifier.
        detected = "edge";
      } else if (ua.includes("safari") && !ua.includes("chrome")) {
        detected = "safari";
      } else if (ua.includes("chrome")) {
        detected = "chrome";
      }

      this.detectedBrowser = detected;
      // Chrome 家族模式默认选中 Chrome，可由用户切换到 360 安全或 360 极速
      this.selectedBrowser = detected === 'chrome' ? 'chrome' : detected;
    },
    // 调试：模拟“被识别为某浏览器”
    simulateBrowser(browserKey) {
      if (!this.isDevelopment) return;
      this.debugDetectedOverride = browserKey;
      // 同步与真实识别时的默认选中逻辑保持一致
      this.selectedBrowser = browserKey === 'chrome' ? 'chrome' : browserKey;
      this.showBrowserMenu = false;
    },
    selectBrowser(browserKey) {
      this.selectedBrowser = browserKey;
      this.showBrowserMenu = false;
    },
    toggleBrowserMenu() {
      this.showBrowserMenu = !this.showBrowserMenu;
    },
    handleClickOutside(event) {
      // 简单的点击外部关闭逻辑，避免DOM查询问题
      const target = event.target;
      const floatingSelector = document.querySelector('.floating-browser-selector');

      if (floatingSelector && !floatingSelector.contains(target)) {
        this.showBrowserMenu = false;
      }
    },
    // Check if URL is a webpage navigation or file download
    isWebpageUrl(url) {
      if (!url) return false;

      // Common webpage domains that should open in new tab
      const webpageDomains = [
        'microsoft.com',
        'microsoftedge.microsoft.com',
        'chromewebstore.google.com',
        'chrome.google.com',
        'addons.mozilla.org',
        'addons.opera.com'
      ];

      // Check if URL contains any of the webpage domains
      return webpageDomains.some(domain => url.includes(domain));
    },
    startDownload() {
      if (this.currentBrowserConfig.download_url) {
        const url = this.currentBrowserConfig.download_url;
        // Check if this is a webpage navigation or file download
        const isWebpageNavigation = this.isWebpageUrl(url);

        if (isWebpageNavigation) {
          // For webpage navigation, open in new tab
          window.open(url, '_blank');
        } else {
          // For file download, use current tab download behavior
          const link = document.createElement("a");
          link.href = url;
          // The download attribute may be ignored for cross-origin URLs, but it's harmless
          const filename = url.split('/').pop() || 'download.zip';
          link.setAttribute('download', filename);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          // Fallback: if the browser ignores download, navigate in the same tab
          // This keeps behavior consistent without spawning a blank tab
          setTimeout(() => {
            try {
              if (!document.hidden) {
                window.location.href = url;
              }
            } catch (_) {
              window.location.href = url;
            }
          }, 0);
        }
      } else {
        alert("该浏览器暂不支持安装，请更换其他浏览器");
      }
    },
  },
};
</script>

<style scoped>
.download-page {
  background: url("https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/KV_100.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
}

.download-content {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 96px;
  padding-bottom: 60px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.download-header {
  text-align: center;
  margin-bottom: 36px;
}

.download-title {
  font-size: 42px;
  font-weight: 700;
  color: #333d47;
  margin-bottom: 4px;
}

.browser-info {
  margin-bottom: 20px;
}

/* Chrome 家族选择器 */
.chrome-family-container {
  /* background: #fff; */
  border-radius: 12px;
  padding: 18px 20px 8px 20px;
  margin-bottom: 18px;
  /* box-shadow: 0 4px 12px rgba(0,0,0,0.06); */
}

.chrome-family-title {
  font-size: 14px;
  color: #333d47;
  margin-bottom: 12px;
}

.chrome-family-options {
  display: flex;
  justify-content: center;
  gap: 28px;
}

.browser-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.browser-option .browser-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* color: #fff; */
  font-weight: 700;
  user-select: none;
  /* box-shadow: 0 2px 8px rgba(0,0,0,0.05); */
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  /* background-color: #fff; */
  /* border: 1px solid rgba(0,0,0,0.06); */
  position: relative;
}

.browser-option .browser-label {
  font-size: 13px;
  color: #444;
}

.browser-option.active .browser-icon-circle {
  transform: scale(1.18);
  box-shadow: 0 6px 18px rgba(13, 58, 74, 0.25);
}

/* Browser icons are applied via inline style to avoid bundler resolution errors */

/* Active selection red corner badge (SVG) */
.browser-option.active .browser-icon-circle::after {
  content: "";
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="%23ff4d4f" d="M0,0 L24,0 L24,24 Z"/><path d="M13 8 L16 11 L21 4" stroke="%23fff" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-size: contain;
  background-repeat: no-repeat;
}

.detected-browser {
  font-size: 16px;
  color: #111;
  margin-bottom: 16px;
}

.download-btn {
  background: #0d3a4a;
  color: white;
  border: none;
  padding: 14px 36px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 16px;
  transition: background-color 0.3s ease;
}

.download-btn:hover:not(.disabled) {
  background: #1a4a5a;
}

.download-btn.disabled {
  background: #ccc;
  cursor: not-allowed;
}

.download-subtitle {
  color: #111;
  font-size: 16px;
  margin: 0;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 60px;
  background-color: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.step-item {
  flex: 1;
  text-align: center;
}

.step-image {
  margin-bottom: 20px;
}

.step-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 40px;
  height: 40px;
  background: #0d3a4a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 20px;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 18px;
  font-weight: 700;
  color: #333d47;
  margin-bottom: 10px;
}

.step-description {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  text-align: left;
}

/* 悬浮浏览器切换器样式 */
.floating-browser-selector {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
}

.floating-button {
  width: 60px;
  height: 60px;
  background: #0d3a4a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(13, 58, 74, 0.3);
  transition: all 0.3s ease;
  border: none;
}

.floating-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(13, 58, 74, 0.4);
}

.floating-button.active {
  background: #1a5a6b;
}

.browser-icon {
  font-size: 24px;
  color: white;
}

.browser-menu {
  position: absolute;
  bottom: 70px;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.browser-menu-header {
  padding: 15px 20px;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  border-bottom: 1px solid #eee;
}

.browser-menu-item {
  display: block;
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: white;
  color: #666;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.browser-menu-item:last-child {
  border-bottom: none;
}

.browser-menu-item:hover {
  background: #f8f9fa;
  color: #0d3a4a;
}

.browser-menu-item.active {
  background: #0d3a4a;
  color: white;
}

.footer-text {
  /** 文本1 */
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 0px;
  line-height: 17.38px;
  color: rgba(0, 0, 0, 1);
  text-align: center;
  vertical-align: top;
}

/* 移动端弹窗样式 */
.mobile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.mobile-modal-content {
  background: white;
  border-radius: 16px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 320px;
  width: 90%;
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.mobile-modal-icon {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.mobile-modal-icon img {
  width: 120px;
  height: 90px;
  object-fit: contain;
}

.mobile-modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #0d3a4a;
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.mobile-modal-description {
  font-size: 16px;
  color: #0d3a4a;
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 992px) {
  .download-content {
    min-height: 100vh;
  }

  .steps-container {
    flex-direction: column;
    align-items: center;
    gap: 50px;
  }

  .step-item {
    max-width: 500px;
    text-align: center;
  }

  .floating-browser-selector {
    bottom: 20px;
    right: 20px;
  }

  .floating-button {
    width: 50px;
    height: 50px;
  }

  .browser-icon {
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  .download-title {
    font-size: 32px;
  }

  .download-content {
    padding-top: 100px;
    padding-bottom: 60px;
    min-height: 100vh;
  }

  .steps-container {
    padding: 30px 20px;
  }

  .floating-browser-selector {
    bottom: 15px;
    right: 15px;
  }

  .browser-menu {
    min-width: 180px;
  }
}

.edge-fallback-tip {
  text-align: center;
  margin-bottom: 20px;
  font-size: 15px;
  color: #444;
}

.edge-fallback-tip a {
  color: #0d3a4a;
  text-decoration: underline;
  font-weight: 600;
}

/* Version loading state */
.version-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  font-size: 16px;
  color: #666;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #0066CC;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Version error state */
.version-error {
  background: #fff5f5;
  border: 1px solid #ffccc7;
  border-radius: 12px;
  padding: 24px 32px;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
}

.version-error .error-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.version-error .error-message {
  font-size: 16px;
  font-weight: 600;
  color: #cf1322;
  margin-bottom: 8px;
}

.version-error .error-hint {
  font-size: 14px;
  color: #666;
}
</style>
